import type { ArrivalIcon } from '../types';
import { ARRIVAL_ICON } from '../types';

/**
 * The glyph inside an arrival option's circled icon [Figma · `12 · What brings you in today?`, the `icon`
 * instance inside each option row's `Icon circle`].
 *
 * **Drawn inline rather than exported from the file**, for the reason `SearchIcon` and `BackLink` already
 * give: the asset rule covers *content* — logos, photographs, illustrations — and these are line glyphs on a
 * tinted disc, which the design system supplies as tokens. An exported SVG bakes `#00808e` into a file that
 * then stops following the palette, and the row needs the SAME glyph in white on the tinted first row and in
 * `--primary` on the rest. `currentColor` is what lets one component do both.
 *
 * **Geometry is measured, not chosen.** Every glyph sits in a 20 × 20 box and each path below reproduces the
 * bounding box the frame reports for that option's vectors, at all three widths (they are identical):
 *
 * | icon | frame vectors, relative to the 20px box |
 * |---|---|
 * | `search` | 12 × 12 @ 3,3 · 4 × 4 @ 14,14 |
 * | `file` | 13 × 17 @ 3,2 · 5 × 5 @ 12,2 |
 * | `clock` | 15 × 15 @ 3,3 · 3 × 6 @ 10,6 |
 * | `bubble` | 13 × 13 @ 2,3 |
 * | `card` | 18 × 13 @ 1,3 · 18 × 0 @ 1,8 |
 * | `flask` | 5 × 0 @ 8,2 · 12 × 16 @ 4,2 |
 *
 * Coordinates below are therefore inset by half the 1.5px stroke: a path from 3.75 to 15.25 measures 3 to 15
 * once stroked, which is the 12 the frame reports. ⚠️ The glyph interiors are the one part of this screen no
 * check measures — `design-qa/refresh-frames.mjs` stops at an INSTANCE, so `frames/arrival-*.json` records
 * the 20px box and nothing inside it. The table above is the reference a reader would otherwise have to
 * re-derive from the Figma file.
 *
 * `aria-hidden`, because the row's own title says what the option is. Six glyphs for seven options: the
 * frame draws the same file-with-a-folded-corner for *Reconfirm my assignment information* and for
 * *Complete my onboarding paperwork / I-9*.
 */
export interface ArrivalOptionIconProps {
  readonly icon: ArrivalIcon;
}

const GLYPH: Record<ArrivalIcon, React.ReactElement> = {
  // A magnifier: the circle 3→15, the handle 14→18 on both axes.
  [ARRIVAL_ICON.search]: (
    <>
      <circle cx="9" cy="9" r="5.25" />
      <path d="M14.75 14.75 17.25 17.25" />
    </>
  ),
  // A page with a folded corner. The fold reaches 0.5px past the page's own right edge in the frame too.
  [ARRIVAL_ICON.file]: (
    <>
      <path d="M12.75 2.75H5.25a1.5 1.5 0 0 0-1.5 1.5v12.5a1.5 1.5 0 0 0 1.5 1.5h8.5a1.5 1.5 0 0 0 1.5-1.5V6.25z" />
      <path d="M12.75 2.75v3.5h3.5" />
    </>
  ),
  // A clock: the dial 3→18, the hands from 12 o'clock through the centre and out to 3.
  [ARRIVAL_ICON.clock]: (
    <>
      <circle cx="10.5" cy="10.5" r="6.75" />
      <path d="M10.75 6.75v4.5h1.5" />
    </>
  ),
  // A speech bubble, tail to the bottom-left — which is why its box starts a pixel left of the others'.
  [ARRIVAL_ICON.bubble]: (
    <path d="M14.25 9.25a5.5 5.5 0 0 1-7.9 4.96l-3.6 1.04 1.04-3.6a5.5 5.5 0 1 1 10.46-2.4z" />
  ),
  // A payment card: the body 1→19 by 3→16, the magnetic stripe running its full width at 8.
  [ARRIVAL_ICON.card]: (
    <>
      <rect height="11.5" rx="2" width="16.5" x="1.75" y="3.75" />
      <path d="M1.75 8.75h16.5" />
    </>
  ),
  // A conical flask: the rim a 5px rule at the top, the body tapering to the base.
  [ARRIVAL_ICON.flask]: (
    <>
      <path d="M8.75 2.75h3.5" />
      <path d="M11.6 2.75v5.9c0 .38.11.75.31 1.07l2.9 4.53a2 2 0 0 1-1.68 3.08H6.87a2 2 0 0 1-1.68-3.08l2.9-4.53c.2-.32.31-.69.31-1.07V2.75" />
    </>
  ),
};

export function ArrivalOptionIcon({ icon }: ArrivalOptionIconProps) {
  return (
    <svg
      aria-hidden="true"
      className="size-5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      viewBox="0 0 20 20"
    >
      {GLYPH[icon]}
    </svg>
  );
}
